/**
 * Reframe — aim the 9:16 crop by hand, per shot.
 *
 * The automatic camera stack occasionally aims the window at a microphone, or
 * leaves the speaker tiny in a wide shot with dead space below. "Polish camera"
 * answers that by re-rolling the WHOLE clip blindly. This panel is the aimed
 * answer: the operator sees the FULL source frame with the crop box drawn on
 * it, drags the box where it belongs, and only that span changes.
 *
 * Two things make it honest:
 *   - the frame is the full 16:9 SOURCE, never a still of the rendered clip
 *     (that one is already cropped — aiming against it would be aiming at the
 *     mistake);
 *   - the live panel on the right is the same box math the renderer uses, so
 *     what you see there is what ships.
 */
import { Camera, Check, Crosshair, Loader2, RotateCcw, Snowflake, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  framingFrameUrl,
  getFraming,
  reframe,
  saveFramingOverrides,
  type FramingOverride,
  type ReframeShot,
  type ReframeState,
} from "../api";

const MIN_ZOOM = 1.0;
const MAX_ZOOM = 1.5;
/** Above this a freeze reads as a still image rather than a held beat. */
const LONG_FREEZE_SEC = 4;

const fmt = (s: number): string => {
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, "0")}`;
};

/** The crop box as fractions of the source frame — the renderer's geometry:
 *  height is 1/zoom of the source, width is that height at 9:16. */
function boxFor(
  cx: number,
  cy: number,
  zoom: number,
  srcW: number,
  srcH: number,
): { w: number; h: number; left: number; top: number } {
  const h = Math.min(1, 1 / zoom);
  const w = Math.min(1, ((srcH * h) * (9 / 16)) / srcW);
  const left = Math.max(0, Math.min(1 - w, cx - w / 2));
  const top = Math.max(0, Math.min(1 - h, cy - h / 2));
  return { w, h, left, top };
}

interface Draft {
  centerX: number;
  centerY: number;
  zoom: number;
}

export default function ReframePanel({
  jobId,
  onClose,
  onApplied,
}: {
  jobId: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [state, setState] = useState<ReframeState | null>(null);
  const [overrides, setOverrides] = useState<FramingOverride[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    getFraming(jobId)
      .then((s) => {
        if (!alive) return;
        setState(s);
        setOverrides(s.overrides);
      })
      .catch((e) => alive && setErr(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; };
  }, [jobId]);

  const shots = state?.shots ?? [];
  const srcW = state?.source.width ?? 1920;
  const srcH = state?.source.height ?? 1080;
  const shot = selected !== null ? shots.find((s) => s.index === selected) ?? null : null;
  // Track times are clip-content-relative; the player's clock includes any
  // baked cover-hold. Display in the player's clock so "the bad bit at 0:46"
  // means the same thing on both sides. Overrides stay on ABSOLUTE source time.
  const playerT = (clipSec: number): number => clipSec + (state?.playerOffsetSec ?? 0);

  /** The override covering a shot, if any. */
  const overrideFor = useCallback(
    (s: ReframeShot): FramingOverride | undefined =>
      overrides.find((o) => o.fromSec <= s.absStartSec + 0.05 && o.toSec >= s.absEndSec - 0.05),
    [overrides],
  );

  const openShot = (s: ReframeShot) => {
    const o = overrideFor(s);
    setSelected(s.index);
    setDraft({
      centerX: o?.action === "aim" ? (o.centerX ?? s.centerX) : s.centerX,
      centerY: o?.action === "aim" ? (o.centerY ?? s.centerY) : s.centerY,
      zoom: o?.action === "aim" ? (o.zoom ?? s.zoom) : s.zoom,
    });
  };

  /** Replace whatever covers this shot with `next` (or clear it). */
  const setShotOverride = (s: ReframeShot, next: FramingOverride | null) => {
    setOverrides((prev) => {
      const kept = prev.filter((o) => o.toSec <= s.absStartSec + 0.05 || o.fromSec >= s.absEndSec - 0.05);
      return next ? [...kept, next].sort((a, b) => a.fromSec - b.fromSec) : kept;
    });
    setDirty(true);
  };

  const commitAim = () => {
    if (!shot || !draft) return;
    setShotOverride(shot, {
      fromSec: shot.absStartSec,
      toSec: shot.absEndSec,
      action: "aim",
      centerX: draft.centerX,
      centerY: draft.centerY,
      zoom: draft.zoom,
    });
    setSelected(null);
  };

  const persist = async (): Promise<FramingOverride[]> => {
    const stored = await saveFramingOverrides(jobId, overrides);
    setOverrides(stored);
    setDirty(false);
    return stored;
  };

  const apply = async () => {
    setBusy(true);
    setErr(null);
    try {
      const stored = await persist();
      if (stored.length === 0) throw new Error("aim or freeze a shot first");
      await reframe(jobId);
      onApplied();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const changedCount = overrides.length;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200, background: "rgba(4,6,12,.78)",
        backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(16,18,27,.96)", border: "1px solid rgba(255,255,255,.08)",
          borderRadius: 20, width: "min(1180px, 96vw)", maxHeight: "92vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 22px", borderBottom: "1px solid rgba(255,255,255,.06)",
        }}>
          <div>
            <div style={{
              fontSize: 12, fontWeight: 700, color: "var(--ql-yellow)",
              textTransform: "uppercase", letterSpacing: ".08em",
            }}>
              Reframe
            </div>
            <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 3 }}>
              {shot
                ? `Shot ${shot.index + 1} · ${fmt(playerT(shot.startSec))}–${fmt(playerT(shot.endSec))} — drag the box where it belongs`
                : "Pick the shot that's framed wrong. Captions, timing and music never change."}
            </div>
          </div>
          <button onClick={onClose} style={iconBtn} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div style={{ overflowY: "auto", padding: 22, flex: 1 }}>
          {err && (
            <div style={{
              background: "rgba(239,68,68,.10)", border: "1px solid rgba(239,68,68,.28)",
              color: "#fca5a5", borderRadius: 12, padding: "10px 14px", fontSize: 13, marginBottom: 16,
            }}>
              {err}
            </div>
          )}

          {!state && !err && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--ql-fg-muted)", fontSize: 13 }}>
              <Loader2 size={15} className="ql-spin" /> Reading the clip's shots…
            </div>
          )}

          {state && !state.source.available && (
            <div style={{
              background: "rgba(255,213,0,.06)", border: "1px solid rgba(255,213,0,.22)",
              borderRadius: 12, padding: "14px 16px", fontSize: 13, color: "var(--ql-fg-muted)",
            }}>
              <strong style={{ color: "var(--ql-fg)" }}>Can't show source frames.</strong>{" "}
              {state.source.reason}
            </div>
          )}

          {state && state.source.available && !state.hasTrack && (
            <div style={{ fontSize: 13, color: "var(--ql-fg-muted)" }}>
              This clip has no saved shot track, so there are no shots to aim. Run{" "}
              <strong style={{ color: "var(--ql-fg)" }}>Polish camera</strong> once — it writes one.
            </div>
          )}

          {state && state.source.available && shot && draft && (
            <ShotEditor
              jobId={jobId}
              shot={shot}
              draft={draft}
              setDraft={setDraft}
              srcW={srcW}
              srcH={srcH}
              playerOffsetSec={state.playerOffsetSec}
              onCancel={() => setSelected(null)}
              onCommit={commitAim}
            />
          )}

          {state && state.source.available && state.hasTrack && !shot && (
            <div style={{ display: "grid", gap: 10 }}>
              {shots.map((s) => {
                const o = overrideFor(s);
                const long = o?.action === "freeze" && s.endSec - s.startSec > LONG_FREEZE_SEC;
                return (
                  <div
                    key={s.index}
                    style={{
                      display: "flex", alignItems: "center", gap: 14, padding: 10,
                      border: `1px solid ${o ? "rgba(255,213,0,.35)" : "rgba(255,255,255,.07)"}`,
                      borderRadius: 14, background: o ? "rgba(255,213,0,.05)" : "rgba(255,255,255,.02)",
                    }}
                  >
                    <ShotThumb
                      jobId={jobId}
                      absSec={s.absStartSec + (s.absEndSec - s.absStartSec) / 2}
                      box={boxFor(
                        o?.action === "aim" ? (o.centerX ?? s.centerX) : s.centerX,
                        o?.action === "aim" ? (o.centerY ?? s.centerY) : s.centerY,
                        o?.action === "aim" ? (o.zoom ?? s.zoom) : s.zoom,
                        srcW, srcH,
                      )}
                      frozen={o?.action === "freeze"}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Shot {s.index + 1}
                        <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>
                          {" "}· {fmt(playerT(s.startSec))}–{fmt(playerT(s.endSec))}
                        </span>
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", marginTop: 3 }}>
                        {o?.action === "aim" && `Aimed by hand · x ${(o.centerX ?? 0).toFixed(2)}${(o.zoom ?? 1) > 1.01 ? ` · zoom ${(o.zoom ?? 1).toFixed(2)}` : ""}`}
                        {o?.action === "freeze" && `Frozen · holds the speaker for ${(s.endSec - s.startSec).toFixed(1)}s`}
                        {!o && `Automatic · x ${s.centerX.toFixed(2)}`}
                      </div>
                      {long && (
                        <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 3 }}>
                          Over {LONG_FREEZE_SEC}s — this will read as a still image.
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => openShot(s)} style={smallBtn(o?.action === "aim")} title="Aim the crop box on this shot">
                        <Crosshair size={13} /> Aim
                      </button>
                      <button
                        onClick={() =>
                          setShotOverride(s, { fromSec: s.absStartSec, toSec: s.absEndSec, action: "freeze" })
                        }
                        style={smallBtn(o?.action === "freeze")}
                        title="Hold the speaker's face across this shot (for real B-roll)"
                      >
                        <Snowflake size={13} /> Freeze
                      </button>
                      <button
                        onClick={() => setShotOverride(s, null)}
                        disabled={!o}
                        style={{ ...smallBtn(false), opacity: o ? 1 : 0.35, cursor: o ? "pointer" : "default" }}
                        title="Back to the automatic framing"
                      >
                        <RotateCcw size={13} /> Auto
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {state?.source.available && state.hasTrack && !shot && (
          <footer style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            padding: "14px 22px", borderTop: "1px solid rgba(255,255,255,.06)",
          }}>
            <div style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>
              {changedCount === 0
                ? "No changes yet."
                : `${changedCount} shot${changedCount === 1 ? "" : "s"} changed${dirty ? " · unsaved" : ""}`}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => void persist()} disabled={!dirty || busy} style={{ ...ghostBtn, opacity: dirty && !busy ? 1 : 0.4 }}>
                Save
              </button>
              <button onClick={() => void apply()} disabled={busy || changedCount === 0} style={{
                ...ghostBtn,
                border: "none", color: "#000",
                background: busy || changedCount === 0 ? "rgba(255,255,255,.10)" : "linear-gradient(135deg,#FFD500,#FFA500)",
                fontWeight: 700,
              }}>
                {busy ? <Loader2 size={14} className="ql-spin" /> : <Camera size={14} />}
                Apply &amp; re-render
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

/** Small source still with the crop box drawn on it. */
function ShotThumb({
  jobId, absSec, box, frozen,
}: {
  jobId: string;
  absSec: number;
  box: { w: number; h: number; left: number; top: number };
  frozen?: boolean;
}) {
  return (
    <div style={{
      position: "relative", width: 148, aspectRatio: "16/9", flexShrink: 0,
      borderRadius: 8, overflow: "hidden", background: "rgba(255,255,255,.04)",
    }}>
      <img
        src={framingFrameUrl(jobId, absSec, 480)}
        alt=""
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "cover", opacity: frozen ? 0.4 : 1 }}
      />
      <div style={{
        position: "absolute",
        left: `${box.left * 100}%`, top: `${box.top * 100}%`,
        width: `${box.w * 100}%`, height: `${box.h * 100}%`,
        border: `2px solid ${frozen ? "rgba(147,197,253,.9)" : "var(--ql-yellow)"}`,
        borderRadius: 2, boxShadow: "0 0 0 9999px rgba(0,0,0,.45)",
      }} />
      {frozen && (
        <Snowflake
          size={18}
          style={{ position: "absolute", right: 6, top: 6, color: "#93c5fd" }}
        />
      )}
    </div>
  );
}

/** Full-frame aim view: drag the box, slide the zoom, watch the 9:16 result. */
function ShotEditor({
  jobId, shot, draft, setDraft, srcW, srcH, playerOffsetSec, onCancel, onCommit,
}: {
  jobId: string;
  shot: ReframeShot;
  draft: Draft;
  setDraft: (d: Draft) => void;
  srcW: number;
  srcH: number;
  playerOffsetSec: number;
  onCancel: () => void;
  onCommit: () => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);
  const [atSec, setAtSec] = useState(shot.absStartSec + (shot.absEndSec - shot.absStartSec) / 2);

  const box = useMemo(
    () => boxFor(draft.centerX, draft.centerY, draft.zoom, srcW, srcH),
    [draft, srcW, srcH],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, cx: draft.centerX, cy: draft.centerY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const el = frameRef.current;
    if (!d || !el) return;
    const r = el.getBoundingClientRect();
    const nx = d.cx + (e.clientX - d.x) / r.width;
    const ny = d.cy + (e.clientY - d.y) / r.height;
    setDraft({
      ...draft,
      centerX: Math.max(box.w / 2, Math.min(1 - box.w / 2, nx)),
      centerY: Math.max(box.h / 2, Math.min(1 - box.h / 2, ny)),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  const frameSrc = framingFrameUrl(jobId, atSec, 1280);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 220px", gap: 20, alignItems: "start" }}>
      <div>
        <div
          ref={frameRef}
          style={{
            position: "relative", width: "100%", aspectRatio: `${srcW}/${srcH}`,
            borderRadius: 12, overflow: "hidden", background: "#000", userSelect: "none", touchAction: "none",
          }}
        >
          <img src={frameSrc} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 0 9999px rgba(0,0,0,.5)", pointerEvents: "none" }} />
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              position: "absolute", cursor: "grab",
              left: `${box.left * 100}%`, top: `${box.top * 100}%`,
              width: `${box.w * 100}%`, height: `${box.h * 100}%`,
              border: "2px solid var(--ql-yellow)",
              boxShadow: "0 0 0 9999px rgba(0,0,0,.0)",
              background: "rgba(255,213,0,.06)",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "var(--ql-fg-muted)", display: "flex", alignItems: "center", gap: 8 }}>
            Zoom
            <input
              type="range" min={MIN_ZOOM} max={MAX_ZOOM} step={0.01} value={draft.zoom}
              onChange={(e) => setDraft({ ...draft, zoom: Number(e.target.value) })}
              style={{ width: 150, accentColor: "var(--ql-yellow)" }}
            />
            <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 11.5 }}>{draft.zoom.toFixed(2)}×</span>
          </label>
          <label style={{ fontSize: 12, color: "var(--ql-fg-muted)", display: "flex", alignItems: "center", gap: 8 }}>
            Frame
            <input
              type="range"
              min={shot.absStartSec} max={Math.max(shot.absStartSec + 0.1, shot.absEndSec - 0.1)} step={0.1}
              value={atSec}
              onChange={(e) => setAtSec(Number(e.target.value))}
              style={{ width: 150, accentColor: "var(--ql-yellow)" }}
            />
            <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 11.5 }}>
              {fmt(shot.startSec + (atSec - shot.absStartSec) + playerOffsetSec)}
            </span>
          </label>
        </div>
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", marginTop: 8 }}>
          Check a few frames across the shot before committing — the box stays put for the whole shot.
        </div>
      </div>

      <div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "var(--ql-fg-muted)",
          textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8,
        }}>
          What ships
        </div>
        <div style={{
          position: "relative", width: "100%", aspectRatio: "9/16",
          borderRadius: 10, overflow: "hidden", background: "#000",
          border: "1px solid rgba(255,255,255,.10)",
        }}>
          <img
            src={frameSrc}
            alt="9:16 result"
            draggable={false}
            style={{
              position: "absolute",
              width: `${100 / box.w}%`, height: `${100 / box.h}%`,
              left: `${-(box.left / box.w) * 100}%`, top: `${-(box.top / box.h) * 100}%`,
              objectFit: "cover",
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          <button onClick={onCommit} style={{
            ...ghostBtn, border: "none", color: "#000", fontWeight: 700,
            background: "linear-gradient(135deg,#FFD500,#FFA500)", justifyContent: "center",
          }}>
            <Check size={14} /> Use this framing
          </button>
          <button onClick={onCancel} style={{ ...ghostBtn, justifyContent: "center" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)",
  color: "var(--ql-fg-muted)", borderRadius: 10, width: 32, height: 32,
  display: "grid", placeItems: "center", cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "10px 16px", borderRadius: 12, fontSize: 13, fontWeight: 600,
  fontFamily: "inherit", cursor: "pointer",
  background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.10)",
  color: "var(--ql-fg)",
};

const smallBtn = (active: boolean): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "7px 11px", borderRadius: 9, fontSize: 12, fontWeight: 600,
  fontFamily: "inherit", cursor: "pointer",
  background: active ? "rgba(255,213,0,.16)" : "rgba(255,255,255,.05)",
  border: `1px solid ${active ? "rgba(255,213,0,.45)" : "rgba(255,255,255,.10)"}`,
  color: active ? "var(--ql-yellow)" : "var(--ql-fg)",
});
